import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Grid,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import carteraService, { Cartera, MovimientoCartera, SaldoCartera } from '../../services/carteraService';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface MovimientoConciliacion extends MovimientoCartera {
  conciliado?: boolean;
}

const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};
const getLastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};
const ConciliacionBancaria: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [bancos, setBancos] = useState<Cartera[]>([]);
  const [bancoSeleccionado, setBancoSeleccionado] = useState<Cartera | null>(null);
  const [loadingBancos, setLoadingBancos] = useState(false);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [movimientos, setMovimientos] = useState<MovimientoConciliacion[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fechaDesde, setFechaDesde] = useState<string>(() =>
    getFirstDayOfMonth().toISOString().split('T')[0]
  );
  const [fechaHasta, setFechaHasta] = useState<string>(() =>
    getLastDayOfMonth().toISOString().split('T')[0]
  );
  const [saldoInicialConciliado, setSaldoInicialConciliado] = useState<number | ''>(0);
  const [tabActiva, setTabActiva] = useState(0); // 0: Sin conciliar, 1: Conciliados
  const [saldoExtracto, setSaldoExtracto] = useState<number | ''>(0);
  const [_movimientosOriginales, setMovimientosOriginales] = useState<MovimientoConciliacion[]>([]);
  const [movimientoSeleccionadoKey, setMovimientoSeleccionadoKey] = useState<string | null>(null);
  const [conciliacionConfirmada, setConciliacionConfirmada] = useState(false);
  const [reconciliacionEnProceso, setReconciliacionEnProceso] = useState(false);
  const conciliadosConfirmadosRef = React.useRef<Set<string>>(new Set());

  const getMovimientoKey = (m: MovimientoConciliacion) =>
    `${m.nroFormulario ?? 0}|${m.fechaEmision ?? ''}|${m.nroTipoFormulario ?? ''}|${(m.descripcion ?? '').trim()}|${(m.detalle ?? '').trim()}`;

  const storageKeyConciliados = (nroBanco: number) =>
    `conciliacion-bancaria:${nroBanco}:conciliados`;

  const leerConciliadosPersistidos = (nroBanco: number): Set<string> => {
    try {
      const raw = window.localStorage.getItem(storageKeyConciliados(nroBanco));
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.filter((x) => typeof x === 'string'));
    } catch {
      return new Set();
    }
  };

  const persistirConciliadosPersistidos = (nroBanco: number, keys: Set<string>) => {
    try {
      window.localStorage.setItem(storageKeyConciliados(nroBanco), JSON.stringify(Array.from(keys)));
    } catch {
      // Si localStorage falla (privacidad/entorno), no rompemos la UI.
    }
  };

  useEffect(() => {
    const cargarBancos = async () => {
      setLoadingBancos(true);
      try {
        const list = await carteraService.getCarteraBancosTodos();
        setBancos(list);
      } catch (error: any) {
        console.error('Error al cargar bancos:', error);
        enqueueSnackbar(
          error.response?.data?.message || 'Error al cargar la lista de bancos',
          { variant: 'error' }
        );
        setBancos([]);
      } finally {
        setLoadingBancos(false);
      }
    };
    cargarBancos();
  }, [enqueueSnackbar]);

  const cargarMovimientos = React.useCallback(async () => {
    if (!bancoSeleccionado?.nroBanco) return;
    setLoadingMovimientos(true);
    try {
      // Traemos todos los movimientos del período sin enviar fechas al backend
      const items = await carteraService.getMovimientosConciliacionTodos(bancoSeleccionado.nroBanco);

      const itemsArr = (items as MovimientoConciliacion[]) || [];

      // Base: dejamos `conciliado=false` para todos, porque lo "confirmado para siempre"
      // lo manejamos aparte (localStorage/backend) y así no contamina los cálculos de una nueva conciliación.
      const normalizadosBase = itemsArr.map((m) => ({
        ...m,
        conciliado: false,
      }));

      setMovimientosOriginales(normalizadosBase);

      // Confirmados (para siempre) = persistidos en localStorage + conciliado=true que ya venga desde el backend.
      const confirmadosPersistidos = leerConciliadosPersistidos(bancoSeleccionado.nroBanco);
      const confirmadosBackend = new Set(
        itemsArr.filter((m) => m.conciliado === true).map((m) => getMovimientoKey(m))
      );
      const conciliadosConfirmados = new Set([
        ...Array.from(confirmadosPersistidos),
        ...Array.from(confirmadosBackend),
      ]);

      conciliadosConfirmadosRef.current = conciliadosConfirmados;

      // En esta nueva conciliación, lo que se marca en pantalla vive en `m.conciliado=true`.
      // Lo confirmando para siempre se representa con la tilde usando `conciliadosConfirmadosRef`.
      setMovimientos(normalizadosBase);
      setPage(0);
      setMovimientoSeleccionadoKey(null);
    } catch (error: any) {
      console.error('Error al cargar movimientos:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Error al cargar los movimientos de conciliación',
        { variant: 'error' }
      );
      setMovimientos([]);
    } finally {
      setLoadingMovimientos(false);
    }
  }, [bancoSeleccionado?.nroBanco, enqueueSnackbar]);

  useEffect(() => {
    if (!bancoSeleccionado?.nroBanco) {
      setMovimientos([]);
      setSaldoInicialConciliado(0);
      setSaldoExtracto(0);
      setConciliacionConfirmada(false);
      setReconciliacionEnProceso(false);
      conciliadosConfirmadosRef.current.clear();
      setFechaDesde(getFirstDayOfMonth().toISOString().split('T')[0]);
      setFechaHasta(getLastDayOfMonth().toISOString().split('T')[0]);
      setMovimientosOriginales([]);
      setMovimientoSeleccionadoKey(null);
      setTabActiva(0);
      setPage(0);
      return;
    }

    // Si cambiás de banco, reiniciamos la conciliación confirmada de la sesión.
    setConciliacionConfirmada(false);
    setReconciliacionEnProceso(false);
    conciliadosConfirmadosRef.current.clear();
    cargarMovimientos();
  }, [bancoSeleccionado?.nroBanco, cargarMovimientos]);

  // Cargar el "Saldo Inicial" desde el banco seleccionado (no editable por el usuario)
  useEffect(() => {
    const nroBanco = bancoSeleccionado?.nroBanco;
    if (!nroBanco) return;

    let isMounted = true;
    const cargarSaldoInicialDesdeBanco = async () => {
      try {
        const allSaldos: SaldoCartera[] = [];
        let pageNumber = 1;
        const pageSize = 20;
        let hasMore = true;

        while (hasMore) {
          const res = await carteraService.getSaldos({
            pageNumber,
            pageSize,
          });
          if (res.items?.length) allSaldos.push(...res.items);

          const totalCount = res.totalCount ?? allSaldos.length;
          const totalPages = res.totalPages ?? 1;

          if (!res.items?.length || allSaldos.length >= totalCount || pageNumber >= totalPages) {
            hasMore = false;
          } else {
            pageNumber += 1;
          }
        }

        const saldoItem = allSaldos.find((s) => s.nroBanco === nroBanco);
        if (!isMounted) return;

        const saldo = saldoItem?.saldo ?? 0;
        setSaldoInicialConciliado(saldo);
      } catch (error: any) {
        console.error('Error al cargar saldo inicial:', error);
        if (!isMounted) return;
        enqueueSnackbar(
          error.response?.data?.message || 'Error al cargar el saldo inicial del banco',
          { variant: 'error' }
        );
        setSaldoInicialConciliado(0);
      }
    };

    cargarSaldoInicialDesdeBanco();

    return () => {
      isMounted = false;
    };
  }, [bancoSeleccionado?.nroBanco, enqueueSnackbar]);

  const formatComprobante = (m: MovimientoConciliacion) => {
    if (m.letraSucNum) return m.letraSucNum;
    const { letra, nroSuc, nroComprobante } = m;
    if (!letra || nroSuc === undefined || nroComprobante === undefined) return '-';
    const formatPart = (val: number | string | undefined | null, len: number) => {
      if (val === null || val === undefined) return ''.padStart(len, '0');
      const n = Number(val);
      return !Number.isNaN(n) ? n.toString().padStart(len, '0') : String(val).padStart(len, '0');
    };
    const suc = formatPart(nroSuc, 5);
    const comp = formatPart(nroComprobante, 8);
    return `${(letra || '').trim().toUpperCase()}-${suc}-${comp}`;
  };

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (_e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(10);
    setPage(0);
  };

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: 0 | 1) => {
    setTabActiva(newValue);
    setPage(0);
  };

  const movimientosEnRango = movimientos.filter((m) => {
    if (!m.fechaEmision) return true;
    const fechaMov = new Date(m.fechaEmision);
    if (Number.isNaN(fechaMov.getTime())) return true;

    const desde = fechaDesde ? new Date(fechaDesde) : null;
    const hasta = fechaHasta ? new Date(fechaHasta) : null;

    if (desde && fechaMov < desde) return false;
    if (hasta) {
      const hastaInclusive = new Date(hasta);
      hastaInclusive.setDate(hastaInclusive.getDate() + 1);
      if (fechaMov >= hastaInclusive) return false;
    }

    return true;
  });

  const movimientosConciliados = movimientosEnRango.filter((m) => m.conciliado);

  // Las fechas se bloquean cuando se inicia una conciliación en esta sesión (al marcar el primer movimiento),
  // y se liberan al confirmar o reiniciar.
  const fechasBloqueadas = reconciliacionEnProceso && !conciliacionConfirmada;

  // Sin conciliar: los NO confirmados (incluye marcados con tilde hasta que se confirme).
  // Conciliados: solo los confirmados (con tilde verde).
  const movimientosFiltrados = tabActiva === 0
    ? movimientosEnRango.filter((m) => !conciliadosConfirmadosRef.current.has(getMovimientoKey(m)))
    : movimientosEnRango.filter((m) => conciliadosConfirmadosRef.current.has(getMovimientoKey(m)));

  const saldoInicialConciliadoNum = saldoInicialConciliado === '' ? 0 : saldoInicialConciliado;
  const saldoExtractoNum = saldoExtracto === '' ? 0 : saldoExtracto;

  const totalesDebitos = movimientosConciliados.reduce(
    (acc, m) => acc + (m.debe && m.debe > 0 ? m.debe : 0),
    0
  );
  const totalesCreditos = movimientosConciliados.reduce(
    (acc, m) => acc + (m.haber && m.haber > 0 ? m.haber : 0),
    0
  );
  const cantidadDebitos = movimientosConciliados.filter((m) => m.debe && m.debe > 0).length;
  const cantidadCreditos = movimientosConciliados.filter((m) => m.haber && m.haber > 0).length;

  const saldoConciliado = saldoInicialConciliadoNum + totalesCreditos - totalesDebitos;
  const saldoDelPeriodo = saldoInicialConciliadoNum + totalesDebitos - totalesCreditos;
  const saldoControl = saldoExtractoNum - saldoConciliado;

  const handleMarcar = () => {
    if (conciliacionConfirmada) {
      enqueueSnackbar('La conciliación ya fue confirmada. No se pueden modificar marcas.', {
        variant: 'info',
      });
      return;
    }
    if (movimientoSeleccionadoKey == null) {
      enqueueSnackbar('Seleccione una fila para marcar.', { variant: 'info' });
      return;
    }
    if (conciliadosConfirmadosRef.current.has(movimientoSeleccionadoKey)) {
      enqueueSnackbar('Este movimiento ya está conciliado.', { variant: 'info' });
      return;
    }
    // Bloquear fechas apenas se marca el primer movimiento en esta sesión.
    if (!reconciliacionEnProceso) setReconciliacionEnProceso(true);
    setMovimientos((prev) =>
      prev.map((m) =>
        getMovimientoKey(m) === movimientoSeleccionadoKey ? { ...m, conciliado: true } : m
      )
    );
  };

  const handleDesmarcar = () => {
    if (conciliacionConfirmada) {
      enqueueSnackbar('La conciliación ya fue confirmada. No se pueden modificar marcas.', {
        variant: 'info',
      });
      return;
    }
    if (movimientoSeleccionadoKey == null) {
      enqueueSnackbar('Seleccione una fila para desmarcar.', { variant: 'info' });
      return;
    }
    if (conciliadosConfirmadosRef.current.has(movimientoSeleccionadoKey)) {
      enqueueSnackbar('Este movimiento ya está conciliado.', { variant: 'info' });
      return;
    }
    setMovimientos((prev) =>
      prev.map((m) =>
        getMovimientoKey(m) === movimientoSeleccionadoKey ? { ...m, conciliado: false } : m
      )
    );
  };

  const handleReiniciarConciliacion = () => {
    setConciliacionConfirmada(false);
    setReconciliacionEnProceso(false);
    // Recargamos para que se mantengan los conciliados confirmados persistidos
    // y se eliminen las marcas no confirmadas de esta sesión.
    cargarMovimientos();
    setSaldoExtracto(0);
    setSaldoInicialConciliado(0);
    setMovimientoSeleccionadoKey(null);
    setTabActiva(0);
    setPage(0);
    enqueueSnackbar('Conciliación reiniciada.', { variant: 'info' });
  };

  const handleConfirmarConciliacion = () => {
    if (Math.abs(saldoControl) > 0.01) {
      enqueueSnackbar('El Saldo Control debe ser 0 para confirmar la conciliación.', {
        variant: 'warning',
      });
      return;
    }

    if (!bancoSeleccionado?.nroBanco) return;

    const nroBanco = bancoSeleccionado.nroBanco;
    const clavesMarcadas = movimientos
      .filter((m) => m.conciliado === true)
      .map((m) => getMovimientoKey(m));

    const existentes = leerConciliadosPersistidos(nroBanco);
    clavesMarcadas.forEach((k) => existentes.add(k));
    persistirConciliadosPersistidos(nroBanco, existentes);
    conciliadosConfirmadosRef.current = existentes;

    setConciliacionConfirmada(true);
    setReconciliacionEnProceso(false);

    // Volver a la vista inicial (como al abrir la ventana por primera vez).
    setBancoSeleccionado(null);
    setMovimientos([]);
    setMovimientosOriginales([]);
    setMovimientoSeleccionadoKey(null);
    setSaldoInicialConciliado(0);
    setSaldoExtracto(0);
    setTabActiva(0);
    setPage(0);
    setFechaDesde(getFirstDayOfMonth().toISOString().split('T')[0]);
    setFechaHasta(getLastDayOfMonth().toISOString().split('T')[0]);

    enqueueSnackbar('Conciliación confirmada. Los movimientos quedan conciliados.', {
      variant: 'success',
    });
  };

  const paginatedMovimientos = movimientosFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', mb: 3 }}>
            Conciliación Bancaria
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={bancos}
                  getOptionLabel={(opt) =>
                    opt.nombreBanco ? String(opt.nombreBanco) : `Banco ${opt.nroBanco}`
                  }
                  value={bancoSeleccionado as Cartera | undefined}
                  onChange={(_, v) => setBancoSeleccionado(v)}
                  loading={loadingBancos}
                  disableClearable
                  forcePopupIcon={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar banco"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  disabled={fechasBloqueadas}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: fechasBloqueadas ? '#f5f5f5' : '#fafafa',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': { bgcolor: '#ffffff' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  disabled={fechasBloqueadas}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: fechasBloqueadas ? '#f5f5f5' : '#fafafa',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': { bgcolor: '#ffffff' },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Saldo inicial del período (proviene del banco) */}
          <Box sx={{ mb: 3, mt: -2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Saldo Inicial"
                  type="number"
                  value={saldoInicialConciliado}
                  onFocus={() => {
                    if (saldoInicialConciliado === 0) {
                      setSaldoInicialConciliado('');
                    }
                  }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setSaldoInicialConciliado('');
                      return;
                    }
                    const v = parseFloat(raw) || 0;
                    setSaldoInicialConciliado(v);
                  }}
                  inputProps={{ step: 0.01, min: 0 }}
                  disabled={fechasBloqueadas}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f5f5f5',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': { bgcolor: '#ffffff' },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {!bancoSeleccionado ? (
            <Typography variant="body2" color="text.secondary">
              Seleccione un banco para ver las conciliaciones.
            </Typography>
          ) : loadingMovimientos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Tabs value={tabActiva} onChange={handleChangeTab} sx={{ minHeight: 40 }}>
                  <Tab label="Sin conciliar pendientes" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
                  <Tab label="Conciliados" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
                </Tabs>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<RefreshIcon />}
                  onClick={cargarMovimientos}
                  disabled={loadingMovimientos || !bancoSeleccionado}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Actualizar
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>F. Emisión</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 140 }}>Comprobante</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Detalle</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="right">Débitos</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="right">Créditos</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="center">Conciliado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedMovimientos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                            No hay movimientos para mostrar.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedMovimientos.map((m, idx) => {
                        const key = getMovimientoKey(m);
                        return (
                          <TableRow
                            key={m.nroFormulario ?? idx}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor:
                                movimientoSeleccionadoKey === key
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'inherit',
                              '&:hover': {
                                bgcolor:
                                  movimientoSeleccionadoKey === key
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : '#f9f9f9',
                              },
                            }}
                            onClick={() => setMovimientoSeleccionadoKey(key)}
                            onDoubleClick={() => {
                              if (conciliacionConfirmada) return;
                              if (conciliadosConfirmadosRef.current.has(key)) return;
                              const mmActual = movimientos.find((mm) => getMovimientoKey(mm) === key);
                              const willMark = !(mmActual?.conciliado === true);
                              if (willMark && !reconciliacionEnProceso) setReconciliacionEnProceso(true);
                              setMovimientos((prev) =>
                                prev.map((mm) =>
                                  getMovimientoKey(mm) === key
                                    ? { ...mm, conciliado: !(mm.conciliado === true) }
                                    : mm
                                )
                              );
                            }}
                          >
                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {formatDate(m.fechaEmision) || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {m.descripcion || m.nombre || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {formatComprobante(m)}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {m.detalle || '-'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color:
                                  m.debe != null && m.debe > 0 ? '#1976d2' : 'inherit',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              }}
                            >
                              {m.debe != null && m.debe > 0
                                ? formatCurrency(m.debe)
                                : '-'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color:
                                  m.haber != null && m.haber > 0 ? '#1976d2' : 'inherit',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              }}
                            >
                              {m.haber != null && m.haber > 0
                                ? formatCurrency(m.haber)
                                : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {(
                                m.conciliado === true ||
                                conciliadosConfirmadosRef.current.has(key)
                              ) ? (
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 22 }} />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}

                    {movimientosEnRango.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={4} />
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            borderTop: '2px solid #e0e0e0',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          }}
                        >
                          {formatCurrency(totalesDebitos || 0)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            borderTop: '2px solid #e0e0e0',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          }}
                        >
                          {formatCurrency(totalesCreditos || 0)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10]}
                  component="div"
                  count={movimientosFiltrados.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>

              <Box
                sx={{
                  mt: 2,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.3fr' },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    bgcolor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Conciliación
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '0.9fr 0.9fr 1.2fr',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box sx={{ p: 1 }} />
                    <Box
                      sx={{
                        p: 1,
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        textAlign: 'center',
                        bgcolor: '#f5f5f5',
                        borderLeft: '1px solid #e0e0e0',
                      }}
                    >
                      Nro Transacciones
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        textAlign: 'center',
                        bgcolor: '#f5f5f5',
                        borderLeft: '1px solid #e0e0e0',
                      }}
                    >
                      Monto
                    </Box>

                    <Box sx={{ p: 1, fontWeight: 600 }}>Debe</Box>
                    <Box
                      sx={{
                        p: 1,
                        textAlign: 'right',
                        borderLeft: '1px solid #e0e0e0',
                      }}
                    >
                      {cantidadDebitos}
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        textAlign: 'right',
                        borderLeft: '1px solid #e0e0e0',
                      }}
                    >
                      {formatCurrency(totalesDebitos || 0)}
                    </Box>

                    <Box sx={{ p: 1, fontWeight: 600, borderTop: '1px solid #e0e0e0' }}>Haber</Box>
                    <Box
                      sx={{
                        p: 1,
                        textAlign: 'right',
                        borderLeft: '1px solid #e0e0e0',
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      {cantidadCreditos}
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        textAlign: 'right',
                        borderLeft: '1px solid #e0e0e0',
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      {formatCurrency(totalesCreditos || 0)}
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    bgcolor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Saldos
                  </Typography>
                  <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Saldo del Período"
                          value={formatCurrency(saldoDelPeriodo || 0)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Saldo Extracto"
                          type="number"
                          value={saldoExtracto}
                          onFocus={() => {
                            if (saldoExtracto === 0) setSaldoExtracto('');
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              setSaldoExtracto('');
                              return;
                            }
                            setSaldoExtracto(Number.parseFloat(raw) || 0);
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 0.01 }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Saldo Conciliado"
                          value={formatCurrency(saldoConciliado || 0)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Saldo Control"
                          value={formatCurrency(saldoControl || 0)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          InputProps={{ readOnly: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor:
                                  Math.abs(saldoControl) > 0.01 ? '#d32f2f' : '#2e7d32',
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMarcar}
                  disabled={conciliacionConfirmada}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Marcar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDesmarcar}
                  disabled={conciliacionConfirmada}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Desmarcar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={handleReiniciarConciliacion}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Reiniciar Conciliación
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={handleConfirmarConciliacion}
                  disabled={conciliacionConfirmada || Math.abs(saldoControl) > 0.01}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Confirmar Conciliación
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConciliacionBancaria;
